/**
 * CommandPalette — the ⌘K "search or jump to" control from the handoff (element
 * C). Optional/new: no existing behaviour depends on it. Fuzzy-matches page +
 * subtab names (access-filtered exactly like the nav) and navigates on Enter.
 *
 * Opened by Cmd/Ctrl+K anywhere, or by calling openCommandPalette() from the
 * shell search boxes (desktop + mobile).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router';
import { Search, CornerDownLeft } from 'lucide-react';
import { HUB_SUBTAB_TABS, NAV_TABS, resolveTabAccess, showInSidebar } from '@/lib/nav';
import { useAccess } from '@/lib/access';

let openFn: (() => void) | null = null;
/** Open the palette imperatively (from the shell search boxes). */
export function openCommandPalette(): void {
  openFn?.();
}

interface Item {
  label: string;
  section: string;
  to: string;
}

/** Case-insensitive subsequence match (fuzzy). */
function fuzzy(needle: string, hay: string): boolean {
  if (!needle) return true;
  let i = 0;
  for (let j = 0; j < hay.length && i < needle.length; j++) {
    if (hay[j] === needle[i]) i++;
  }
  return i === needle.length;
}

export function CommandPalette() {
  const navigate = useNavigate();
  const { tabVisibility, userId } = useAccess();
  const isAuthed = !!userId;

  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [idx, setIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Register the imperative opener while mounted.
  useEffect(() => {
    openFn = () => setOpen(true);
    return () => {
      if (openFn) openFn = null;
    };
  }, []);

  // Global Cmd/Ctrl+K toggles the palette; Escape closes it from anywhere
  // (the input has its own handler, but focus may be on the list/backdrop).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setOpen((o) => !o);
      } else if (e.key === 'Escape') {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Clearing the query belongs in render, not in the effect below: an effect
  // clears it in a second commit, so the palette paints once with the PREVIOUS
  // session's query still in the box. The body-scroll lock and the focus stay
  // in the effect — both are DOM side effects with a cleanup.
  const [prevOpen, setPrevOpen] = useState(open);
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) { setQ(''); setIdx(0); }
  }

  // Focus when opening; lock body scroll while open.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const t = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => {
      document.body.style.overflow = prev;
      window.clearTimeout(t);
    };
  }, [open]);

  const items = useMemo<Item[]>(() => {
    const visible = NAV_TABS.filter(
      (t) => t.alwaysShow || showInSidebar(resolveTabAccess(t, tabVisibility), isAuthed),
    );
    const list: Item[] = visible.map((t) => ({ label: t.label, section: t.section, to: t.to }));
    // Performance Summary's URL-addressable subtabs are jump targets too.
    if (visible.some((t) => t.key === 'performanceSummary')) {
      list.push(
        { label: 'Market Calendar', section: 'Overview', to: '/performance-summary/dividends' },
        // ?view= deep-links into the Market Calendar hub's inner tabs, so a
        // 'dividends' (or macro/earnings) search still lands on that view.
        { label: 'Dividends', section: 'Overview', to: '/performance-summary/dividends?view=dividends' },
        { label: 'Macro Calendar', section: 'Overview', to: '/performance-summary/dividends?view=macro' },
        { label: 'Earnings Calendar', section: 'Overview', to: '/performance-summary/dividends?view=earnings' },
        { label: 'Global Indexes', section: 'Overview', to: '/performance-summary/global-indexes' },
        { label: 'Watchlist', section: 'Overview', to: '/performance-summary/watchlist' },
      );
      // Market Themes and Projections honor their own access entries.
      for (const t of HUB_SUBTAB_TABS) {
        if (!showInSidebar(resolveTabAccess(t, tabVisibility), isAuthed)) continue;
        list.push({ label: t.label, section: t.section, to: t.to });
        // Projections addresses its inner panels by path, so each is its own
        // jump target — searching "rebalance" should land on that panel, not
        // on the landing page beside it.
        if (t.key === 'projections') {
          list.push(
            { label: 'Projection fan chart', section: t.section, to: `${t.to}/projection` },
            { label: 'Risk & Dependence', section: t.section, to: `${t.to}/risk` },
            { label: 'Views', section: t.section, to: `${t.to}/views` },
            { label: 'Rebalance', section: t.section, to: `${t.to}/rebalance` },
            { label: 'Candidates', section: t.section, to: `${t.to}/candidates` },
          );
        }
      }
    }
    return list;
  }, [tabVisibility, isAuthed]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return items;
    return items.filter((it) => {
      const hay = `${it.label} ${it.section}`.toLowerCase();
      return hay.includes(s) || fuzzy(s, hay);
    });
  }, [q, items]);

  // Typing moves the highlight back to the first row. In render, so the new
  // filtered list and its highlight land in ONE commit — an effect highlights
  // the old index over the new list for a frame.
  const [prevQ, setPrevQ] = useState(q);
  if (q !== prevQ) {
    setPrevQ(q);
    setIdx(0);
  }

  // Keep the highlighted row in view.
  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-i="${idx}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [idx, filtered]);

  if (!open) return null;

  const go = (item?: Item) => {
    const target = item ?? filtered[idx];
    if (target) {
      navigate(target.to);
      setOpen(false);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setIdx((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      go();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-300 flex items-start justify-center px-4 pt-[12vh]"
      onMouseDown={() => setOpen(false)}
    >
      <div className="absolute inset-0 bg-black/45 backdrop-blur-[1px]" aria-hidden="true" />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Search or jump to"
        onMouseDown={(e) => e.stopPropagation()}
        className="relative w-full max-w-[560px] overflow-hidden rounded-xl border border-border bg-card shadow-2xl"
      >
        <div className="flex items-center gap-3 border-b border-border px-4">
          <Search className="h-[18px] w-[18px] shrink-0 text-muted-foreground" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search or jump to…"
            className="h-12 w-full bg-transparent text-[15px] text-foreground outline-hidden placeholder:text-muted-foreground"
            aria-label="Search pages"
          />
          <kbd className="hidden shrink-0 rounded border border-border px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground sm:inline">
            ESC
          </kbd>
        </div>

        <div ref={listRef} className="max-h-[52vh] overflow-y-auto p-1.5">
          {filtered.length === 0 ? (
            <div className="px-3 py-6 text-center text-sm text-muted-foreground">No matches</div>
          ) : (
            filtered.map((it, i) => (
              <button
                key={it.to}
                type="button"
                data-i={i}
                onMouseEnter={() => setIdx(i)}
                onClick={() => go(it)}
                className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left transition-colors ${
                  i === idx ? 'bg-primary/10 text-foreground' : 'text-foreground hover:bg-muted'
                }`}
              >
                <span className="text-[13.5px] font-medium">{it.label}</span>
                <span className="ml-auto font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                  {it.section}
                </span>
                {i === idx && <CornerDownLeft className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />}
              </button>
            ))
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
